import React, { useEffect, useState } from 'react';
import moment from 'moment';

import { Grid, Typography, Card, CardContent, Divider, Button, useMediaQuery, Modal } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import { ToggleButtonGroup, ToggleButton } from '@material-ui/lab';
import { ViewGridOutline } from 'mdi-material-ui';
import MapIcon from '@material-ui/icons/Map';
import ShowChartIcon from '@material-ui/icons/ShowChart';

import { Page, FieldDropdown, DeviceDropdown, PropertyDropdown } from 'components';

import EventsAtAGlance from 'components/Events/EventsAtAGlance';
import useWindowDimensions from 'hooks/useWindowDimensions';
import DataExportModalForm from './components/Modals/DataExportModal';
import { Link as RouterLink } from 'react-router-dom';

import {
  useTelemetry,
  useProperties,
  useFields,
  useDevices,
  useEvents,
  useSettings,
  useOrganizations,
  useHealthIntervals,
} from 'hooks';
import DashboardFieldMap from './components/DashboardFieldMap';
import Charts from './components/Charts';
import { TimePeriods, BucketPeriods, BucketUnits } from 'utils/ReadingsHelpers';
import { DashboardHelpers } from 'utils';
import TimeSelector from './components/TimeSelector';

import StatsTable from './components/StatsTable/StatsTable';

import _ from 'lodash';
import MobileStatsGrid from './components/StatsTable/MobileStatsGrid';
import ToggleUnits from 'components/Inputs/ToggleUnits';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    width: '100%',
    height: '100%',
    minHeight: '100%',
    flex: 1,
  },
  toggleButtonGroup: {
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      width: '100%',
    },
  },
  toggleButton: {
    minWidth: 100,
    [theme.breakpoints.down('sm')]: {
      minWidth: '33%',
    },
  },
  toggleLabel: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  cardHeader: {
    padding: '0',
    zIndex: 200,
    marginBottom: theme.spacing(3),
  },
  picker: {
    marginTop: theme.spacing(1),
  },
  toggles: {
    display: 'flex',
    direction: 'column',
  },
}));

const Dashboard = () => {
  const pageProps = {};
  pageProps.actionFab = true;
  const title = '';
  const mobileView = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const { height } = useWindowDimensions();

  const classes = useStyles();
  const { t } = useTranslation();
  const { convertPPM, convertTemp, convertUnits, activeDashView, setDashView, isSuper } = useSettings();
  const { getStatusForValue, getSuggestionForInterval } = useHealthIntervals();
  const { fields } = useFields();

  const { properties, selectedProperty } = useProperties();
  const { events } = useEvents();
  const { selectedField } = useFields();
  const { selectedDevice, selectedDevices } = useDevices();

  const { selectedOrganization } = useOrganizations();
  const { telemetryPeriod, setTelemetryPeriod, fieldTelemetry, deviceTelemetry, reqTelemetry, isFetching } =
    useTelemetry();

  const [defaultBucketSettings, setDefaultBucketSettings] = useState({
    period: BucketPeriods.Eight,
    unit: BucketUnits.Hours,
    include_today: true,
  });

  const pageView = activeDashView;

  useEffect(() => {
    if (pageView == 'stats') {
      setTelemetryPeriod(TimePeriods.ZeroDays);
      setDefaultBucketSettings({
        period: BucketPeriods.One,
        unit: BucketUnits.Days,
        timePeriod: TimePeriods.ZeroDays,
        include_today: true,
      });
      // Make telemetry request if we have either a field or a device selected
      if (localSelectedField != undefined || localSelectedDevice != undefined) {
        reqTelemetry(
          { field_id: localSelectedField?.id, hw_address: localSelectedDevice?.hw_address },
          TimePeriods.ZeroDays,
          {
            period: BucketPeriods.One,
            unit: BucketUnits.Days,
            timePeriod: TimePeriods.ZeroDays,
            include_today: true,
          }
        );
      }
    } else if (pageView == 'charts') {
      setTelemetryPeriod(TimePeriods.OneWeek);
      setDefaultBucketSettings({
        period: BucketPeriods.Four,
        unit: BucketUnits.Hours,
        include_today: true,
      });

      // Make telemetry request if we have either a field or a device selected
      if (localSelectedField != undefined || localSelectedDevice != undefined) {
        reqTelemetry(
          { field_id: localSelectedField?.id, hw_address: localSelectedDevice?.hw_address },
          TimePeriods.OneWeek,
          {
            period: BucketPeriods.Four,
            unit: BucketUnits.Hours,
            include_today: true,
          }
        );
      }
    }
  }, [pageView, localSelectedField, localSelectedProperty, localSelectedDevice]);

  const statsTabs = [
    {
      bucketPeriod: BucketPeriods.Seven,
      bucketUnit: BucketUnits.Days,
      timePeriod: TimePeriods.OneWeek,
      index: 0,
      include_today: false,
      label: t('last_week'),
    },
    {
      bucketPeriod: BucketPeriods.One,
      bucketUnit: BucketUnits.Days,
      timePeriod: TimePeriods.OneDay,
      index: 1,
      include_today: false,
      label: t('yesterday'),
    },
    {
      bucketPeriod: BucketPeriods.One,
      bucketUnit: BucketUnits.Days,
      timePeriod: TimePeriods.ZeroDays,
      index: 2,
      include_today: true,
      label: t('today'),
    },
  ];

  const chartsTabs = [
    {
      bucketPeriod: BucketPeriods.Four,
      bucketUnit: BucketUnits.Hours,
      timePeriod: TimePeriods.OneWeek,
      index: 0,
      include_today: true,
      label: t('one_week'),
    },
    {
      bucketPeriod: BucketPeriods.Eight,
      bucketUnit: BucketUnits.Hours,
      timePeriod: TimePeriods.OneMonth,
      index: 1,
      include_today: true,
      label: t('one_month'),
    },
    {
      bucketPeriod: BucketPeriods.One,
      bucketUnit: BucketUnits.Days,
      timePeriod: TimePeriods.ThreeMonth,
      index: 2,
      include_today: false,
      label: t('three_months'),
    },
    {
      bucketPeriod: BucketPeriods.Thirty,
      bucketUnit: BucketUnits.Days,
      timePeriod: TimePeriods.OneYear,
      index: 3,
      include_today: false,
      label: t('one_year'),
    },
    {
      bucketPeriod: BucketPeriods.Thirty,
      bucketUnit: BucketUnits.Days,
      timePeriod: TimePeriods.TwentyYears,
      index: 4,
      include_today: false,
      label: t('lifetime'),
    },
  ];

  const [mobileStatTab, setMobileStatTab] = useState(0);
  // const [depthTabs] = useState(['6"', '12"', '24"']);

  useEffect(() => {
    if (localSelectedProperty?.id !== selectedProperty?.id) {
      setProperty(selectedProperty);
    }
  }, [selectedProperty]);

  useEffect(() => {
    if (localSelectedField?.id !== selectedField?.id) {
      // Only sync if the field actually changed, not when switching views
      if (selectedField && selectedField.id) {
        setField(selectedField);
        reqTelemetry({ field_id: selectedField.id, hw_address: undefined }, telemetryPeriod, defaultBucketSettings);
      }
    }
  }, [selectedField]);

  useEffect(() => {
    if (localSelectedDevice?.id !== selectedDevice?.id) {
      // Sync local state with global state without making telemetry request
      setlocalSelectedDevice(selectedDevice);
    }
  }, [selectedDevice]);

  useEffect(() => {
    if (localSelectedTelemetryPeriod !== telemetryPeriod) {
      setlocalSelectedTelemetryPeriod(telemetryPeriod);
    }
  }, [telemetryPeriod]);

  const [localSelectedProperty, setlocalSelectedProperty] = useState(selectedProperty);
  const [localSelectedDevice, setlocalSelectedDevice] = useState(selectedDevice);
  const [localSelectedField, setlocalSelectedField] = useState(selectedField);
  const [localSelectedFieldDevices, setlocalSelectedFieldDevices] = useState([]);
  const [localSelectedTelemetryPeriod, setlocalSelectedTelemetryPeriod] = useState(telemetryPeriod);
  const [showExportModal, setShowExportModal] = useState(false);
  const localFields = localSelectedProperty ? _.filter(fields, { property_id: localSelectedProperty.id }) : [];

  const setProperty = (property) => {
    setlocalSelectedProperty(property);
    setlocalSelectedField(undefined);
    setlocalSelectedDevice(undefined);
  };

  const setField = (field) => {
    setlocalSelectedField(field);

    // Only clear device selection if the current device doesn't belong to the new field
    if (localSelectedDevice && field) {
      const deviceBelongsToField = localSelectedDevice.field_id === field.id;
      if (!deviceBelongsToField) {
        setlocalSelectedDevice(undefined);
      }
    } else if (!field) {
      setlocalSelectedDevice(undefined);
    }

    if (field && selectedDevices) {
      const fieldDevicesList = _.filter(selectedDevices, { field_id: field.id });
      setlocalSelectedFieldDevices(fieldDevicesList);
      reqTelemetry({ hw_address: undefined, field_id: field.id }, telemetryPeriod, defaultBucketSettings);
    } else {
      setlocalSelectedFieldDevices([]);
    }
  };

  useEffect(() => {
    if (localSelectedField != undefined) {
      setField(localSelectedField);
    }
  }, [localSelectedField, pageView]);

  const setDevice = (device) => {
    setlocalSelectedDevice(device);
    reqTelemetry(
      { hw_address: device?.hw_address, field_id: localSelectedField.id },
      telemetryPeriod,
      defaultBucketSettings
    );
  };

  const updateActiveSettings = (bucketPeriod, bucketUnit, telemetryPeriod, include_today) => {
    setTelemetryPeriod(telemetryPeriod);
    setDefaultBucketSettings({ period: bucketPeriod, unit: bucketUnit, include_today });
    const localBucketSettings = { period: bucketPeriod, unit: bucketUnit, include_today };
    if (
      localSelectedDevice !== undefined &&
      localSelectedDevice.id !== DashboardHelpers.StatusCodes.NoDeviceSelected &&
      localSelectedDevice.id !== undefined &&
      localSelectedField !== undefined &&
      localSelectedField.id !== undefined
    ) {
      reqTelemetry(
        { hw_address: localSelectedDevice.hw_address, field_id: localSelectedField.id },
        telemetryPeriod,
        localBucketSettings
      );
    } else if (
      localSelectedField !== undefined &&
      localSelectedField.id !== DashboardHelpers.StatusCodes.NoFieldSelected &&
      localSelectedField.id !== undefined
    ) {
      reqTelemetry({ hw_address: undefined, field_id: localSelectedField.id }, telemetryPeriod, localBucketSettings);
    }
  };

  const handlePageViewChange = (event, newValue) => {
    if (newValue !== null) {
      if (newValue === 'stats') {
        setTelemetryPeriod(TimePeriods.ZeroDays);
        updateActiveSettings(BucketPeriods.One, BucketUnits.Days, TimePeriods.ZeroDays, true);
      } else if (newValue === 'charts') {
        setTelemetryPeriod(TimePeriods.OneWeek);
        updateActiveSettings(BucketPeriods.Four, BucketUnits.Hours, TimePeriods.OneWeek, true);
      }
      setDashView(newValue);
    }
  };

  let noOrgSelectedPrompt = '';

  if (!selectedOrganization || (selectedOrganization && !selectedOrganization.id && isSuper)) {
    if (pageView === 'map') {
      noOrgSelectedPrompt = (
        <div className={classes.mapNoOrg}>
          <Typography variant="h2">{t('no_org_selected_map_view')}</Typography>
        </div>
      );
    } else if (pageView === 'stats' || pageView === 'charts') {
      noOrgSelectedPrompt = (
        <div className={classes.statsNoOrg}>
          <Typography variant="h2">{t('no_org_selected_stats_view')}</Typography>
          <Typography variant="h4" style={{ margin: 15 }}>
            {t('no_org_selected_stats_prompt')}
          </Typography>
        </div>
      );
    } else {
      noOrgSelectedPrompt = undefined;
    }
  }

  const [showSoilDataError, setShowSoilDataError] = useState(false);

  useEffect(() => {
    handleSoilDataWarning();
  }, [localSelectedField]);

  const handleSoilDataWarning = () => {
    if (!localSelectedField) {
      setShowSoilDataError(false);
    } else if (!localSelectedField.soil_texture || !localSelectedField.soil_density) {
      setShowSoilDataError(true);
    } else {
      let textureTotal =
        Number(localSelectedField.soil_texture['sand']) +
        Number(localSelectedField.soil_texture['silt']) +
        Number(localSelectedField.soil_texture['clay']);

      if (Number(localSelectedField.soil_density) > 0 && textureTotal > 0) {
        setShowSoilDataError(false);
      } else {
        setShowSoilDataError(true);
      }
    }
  };
  return (
    <Page
      title={t(title)}
      className={classes.root}
      key={selectedDevice?.id + selectedField?.id + selectedProperty?.id + '_dashboard'}
    >
      <Modal
        open={showExportModal}
        onClose={() => {}}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <DataExportModalForm
          hide={() => setShowExportModal(false)}
          passedProperty={localSelectedProperty}
          passedField={localSelectedField}
          passedDevice={localSelectedDevice}
        />
      </Modal>
      <ToggleButtonGroup
        value={pageView}
        exclusive
        className={classes.toggleButtonGroup}
        onChange={handlePageViewChange}
        aria-label="Dashboard View"
      >
        <ToggleButton className={classes.toggleButton} value="stats" aria-label={t('stats')}>
          <ViewGridOutline />
          <Typography className={classes.toggleLabel}>{t('stats')}</Typography>
        </ToggleButton>
        <ToggleButton className={classes.toggleButton} value="map" aria-label={t('map')}>
          <MapIcon />
          <Typography className={classes.toggleLabel}>{t('map')}</Typography>
        </ToggleButton>
        <ToggleButton className={classes.toggleButton} value="charts" aria-label={t('charts')}>
          <ShowChartIcon />
          <Typography className={classes.toggleLabel}>{t('charts')}</Typography>
        </ToggleButton>
      </ToggleButtonGroup>
      <Card>
        <CardContent>
          {noOrgSelectedPrompt}
          {selectedOrganization && selectedOrganization.id && (
            <Grid container direction="row" className={classes.cardHeader}>
              <Grid item sm={12}>
                <Typography variant="h3" className={classes.title}>
                  {t('data_filters')}
                  {!isFetching && (
                    <Button style={{ marginLeft: 50 }} variant="outlined" onClick={() => setShowExportModal(true)}>
                      {t('export_avg_button')}
                    </Button>
                  )}
                </Typography>
              </Grid>
              <Grid item container direction="row" sm={6} style={{ marginTop: 30, marginLeft: 30 }}>
                <Grid item sm={4}>
                  <PropertyDropdown
                    selectedProperty={localSelectedProperty}
                    setSelectedProperty={setProperty}
                    properties={_.filter(
                      properties,
                      (property) => property.organization_id === selectedOrganization.id
                    )}
                    selectedOrganization={selectedOrganization}
                  />
                </Grid>
                <Grid item sm={4}>
                  <FieldDropdown
                    selectedField={localSelectedField}
                    setSelectedField={setField}
                    selectedProperty={localSelectedProperty}
                    selectedOrganization={selectedOrganization}
                    setProperty={(property_id) => setlocalSelectedProperty(_.find(properties, { id: property_id }))}
                  />
                  {showSoilDataError && (
                    <Grid style={{ color: 'red' }} item xs={12}>
                      <span>
                        This field&#39;s soil texture and/or density have not been entered. To improve probe accuracy,
                        please click
                      </span>
                      <RouterLink to="/fields"> here </RouterLink>
                      <span>to set them up</span>
                    </Grid>
                  )}
                </Grid>

                {pageView !== 'map' &&
                  localSelectedField?.id &&
                  localSelectedField?.id !== DashboardHelpers.StatusCodes.NoFieldSelected && (
                    <Grid item container sm={6} direction="row">
                      <Grid item>
                        <DeviceDropdown
                          setSelectedDevice={setDevice}
                          selectedDevice={localSelectedDevice}
                          devices={[
                            { id: DashboardHelpers.StatusCodes.NoDeviceSelected, name: 'Field Average' },
                          ].concat(localSelectedFieldDevices)}
                        />
                      </Grid>
                    </Grid>
                  )}
              </Grid>
              {localSelectedProperty && !mobileView && (
                <Grid item sm={5} style={{ justifySelf: 'flex-end' }}>
                  <EventsAtAGlance
                    events={events}
                    selectedOrg={selectedOrganization}
                    selectedPropertyId={
                      localSelectedProperty &&
                      localSelectedProperty.id !== DashboardHelpers.StatusCodes.NoPropertySelected
                        ? localSelectedProperty.id
                        : undefined
                    }
                    selectedFieldId={
                      localSelectedField && localSelectedField.id !== DashboardHelpers.StatusCodes.NoFieldSelected
                        ? localSelectedField.id
                        : undefined
                    }
                    selectedDevice={
                      localSelectedDevice && localSelectedDevice.id !== DashboardHelpers.StatusCodes.NoDeviceSelected
                        ? localSelectedDevice
                        : undefined
                    }
                    telemetryPeriod={telemetryPeriod}
                  />
                </Grid>
              )}
              <Grid item xs={4} md={2}>
                {pageView !== 'map' && (
                  <div>
                    <ToggleUnits />
                  </div>
                )}
              </Grid>
            </Grid>
          )}
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12}>
              <Typography variant="body2">{moment().format('lll')}</Typography>
              <Divider />
            </Grid>
          </Grid>
          {pageView === 'stats' && (
            <TimeSelector
              onTabClick={updateActiveSettings}
              tabs={statsTabs}
              telemetryPeriod={telemetryPeriod}
              defaultIndex={2}
            />
          )}
          {pageView === 'charts' && (
            <TimeSelector
              onTabClick={updateActiveSettings}
              tabs={chartsTabs}
              telemetryPeriod={telemetryPeriod}
              defaultIndex={0}
            />
          )}
          {pageView === 'map' && (
            <Grid
              container
              spacing={3}
              alignItems="center"
              style={selectedOrganization?.id ? { height: height - 390 } : { height: height - 230 }}
            >
              <Grid item xs={12}>
                <DashboardFieldMap
                  setSelectedField={setField}
                  selectedField={localSelectedField}
                  selectedFieldDevices={localSelectedFieldDevices}
                  fields={localFields}
                  selectedProperty={localSelectedProperty}
                  selectedDevices={selectedDevices}
                  activeFieldDevices={_.filter(selectedDevices, (device) => device.fieldId === localSelectedField?.id)}
                  telemetryAverages={fieldTelemetry}
                  telemetryPeriod={telemetryPeriod}
                />
              </Grid>
            </Grid>
          )}
          {pageView === 'stats' && (
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12}>
                <Divider />
              </Grid>
              {localSelectedField === undefined ||
              localSelectedField?.id === DashboardHelpers.StatusCodes.NoFieldSelected ? (
                <Grid item xs={12}>
                  <Typography variant="h4">{t('select_field_stats')}</Typography>
                </Grid>
              ) : mobileView ? (
                <MobileStatsGrid
                  getStatusForValue={getStatusForValue}
                  getSuggestionForInterval={getSuggestionForInterval}
                  convertPPM={convertPPM}
                  convertTemp={convertTemp}
                  convertUnits={convertUnits}
                  selectedDevice={localSelectedDevice}
                  selectedField={localSelectedField}
                  fieldTelemetry={fieldTelemetry}
                  deviceTelemetry={deviceTelemetry}
                  depthArrayIndex={mobileStatTab}
                  setMobileStatTab={setMobileStatTab}
                ></MobileStatsGrid>
              ) : (
                <StatsTable
                  getStatusForValue={getStatusForValue}
                  getSuggestionForInterval={getSuggestionForInterval}
                  convertPPM={convertPPM}
                  convertTemp={convertTemp}
                  convertUnits={convertUnits}
                  selectedDevice={localSelectedDevice}
                  selectedField={localSelectedField}
                  fieldTelemetry={fieldTelemetry}
                  deviceTelemetry={deviceTelemetry}
                ></StatsTable>
              )}
            </Grid>
          )}
          {pageView === 'charts' && (
            <div>
              {localSelectedField ? (
                <Charts
                  dataObject={() =>
                    DashboardHelpers.getDataSet(
                      deviceTelemetry ? deviceTelemetry[localSelectedDevice?.hw_address] : [],
                      fieldTelemetry ? fieldTelemetry[localSelectedField?.id] : [],
                      localSelectedDevice,
                      localSelectedField,
                      telemetryPeriod
                    )
                  }
                  isFetching={isFetching}
                />
              ) : (
                <Grid item xs={12} style={{ paddingTop: 15 }}>
                  <Typography variant="h4">{t('select_field_charts')}</Typography>
                </Grid>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </Page>
  );
};

export default Dashboard;
